/**
 * Pickify Personalization Engine
 *
 * Aligns results with user intent based on:
 * 1. Inbound signals from ads (URL parameters)
 * 2. On-site behavior tracking (clicks, quiz results)
 *
 * The personalization is silent - it should feel like the quiz
 * "magically" found exactly what the user wanted.
 */

// Constants
const STORAGE_KEYS = {
  INBOUND_SIGNAL: 'pickify_inbound_signal',
  USER_PROFILE: 'pickify_user_profile',
  UID: 'pickify_uid'
};

/**
 * Get or create a persistent anonymous user ID.
 * No PII — just a random string that persists across sessions.
 * Used for cross-session analytics and server-side profile sync.
 */
export const getOrCreateUID = () => {
  try {
    let uid = localStorage.getItem(STORAGE_KEYS.UID);
    if (!uid) {
      uid = `u_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(STORAGE_KEYS.UID, uid);
    }
    return uid;
  } catch {
    return null;
  }
};

const SIGNAL_EXPIRY_HOURS = 24;
const CLICK_SCORE = 2;
const BUY_CLICK_SCORE = 5;
const SAVE_SCORE = 3;
const SWIPE_LEFT_SCORE = -1;
const QUIZ_RESULT_SCORE = 3;

// Valid categories that can be used for personalization
const VALID_CATEGORIES = [
  'tech', 'fashion', 'sports', 'books', 'food', 'gaming',
  'art', 'travel', 'music', 'beauty', 'home', 'outdoor',
  'wellness', 'jewelry', 'kids', 'pets'
];

// Category aliases (map ad interests to internal categories)
const CATEGORY_ALIASES = {
  'gadgets': 'tech',
  'technology': 'tech',
  'electronics': 'tech',
  'style': 'fashion',
  'clothing': 'fashion',
  'fitness': 'sports',
  'reading': 'books',
  'cooking': 'food',
  'culinary': 'food',
  'games': 'gaming',
  'creative': 'art',
  'crafts': 'art',
  'vacation': 'travel',
  'adventure': 'travel',
  'skincare': 'beauty',
  'cosmetics': 'beauty',
  'decor': 'home',
  'furniture': 'home',
  'nature': 'outdoor',
  'camping': 'outdoor',
  'health': 'wellness',
  'selfcare': 'wellness',
  'accessories': 'jewelry',
  'children': 'kids',
  'toys': 'kids',
  'animals': 'pets'
};

/**
 * Normalize a category string to a valid internal category
 */
const normalizeCategory = (category) => {
  if (!category) return null;
  const lower = category.toLowerCase().trim();

  // Check if it's already a valid category
  if (VALID_CATEGORIES.includes(lower)) return lower;

  // Check aliases
  if (CATEGORY_ALIASES[lower]) return CATEGORY_ALIASES[lower];

  return null;
};

/**
 * Check if a timestamp has expired
 */
const isExpired = (timestamp, hoursToExpire = SIGNAL_EXPIRY_HOURS) => {
  if (!timestamp) return true;
  const expiry = new Date(timestamp);
  expiry.setHours(expiry.getHours() + hoursToExpire);
  return new Date() > expiry;
};

// ============================================================
// Task 1: Signal Listener (Inbound Intent from Ads)
// ============================================================

/**
 * Capture inbound signals from URL parameters
 * Checks for: ?interest=, ?category=, ?intent=
 *
 * Should be called on app initialization
 */
export const captureInboundSignal = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);

    // Check multiple possible parameter names
    const interest = urlParams.get('interest') ||
                     urlParams.get('category') ||
                     urlParams.get('intent') ||
                     urlParams.get('i');

    if (!interest) return null;

    const normalizedCategory = normalizeCategory(interest);
    if (!normalizedCategory) {
      console.log(`🎯 Inbound signal ignored - unknown category: ${interest}`);
      return null;
    }

    // Get UTM source for attribution
    const utmSource = urlParams.get('utm_source') || 'direct';
    const utmCampaign = urlParams.get('utm_campaign') || null;

    const signal = {
      category: normalizedCategory,
      originalValue: interest,
      source: utmSource,
      campaign: utmCampaign,
      capturedAt: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEYS.INBOUND_SIGNAL, JSON.stringify(signal));
    console.log(`🎯 Inbound signal captured: ${normalizedCategory} (from: ${interest})`);

    return signal;
  } catch (error) {
    console.error('Failed to capture inbound signal:', error);
    return null;
  }
};

/**
 * Get the current inbound signal if not expired
 */
export const getInboundSignal = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.INBOUND_SIGNAL);
    if (!stored) return null;

    const signal = JSON.parse(stored);

    // Check expiry
    if (isExpired(signal.capturedAt, SIGNAL_EXPIRY_HOURS)) {
      localStorage.removeItem(STORAGE_KEYS.INBOUND_SIGNAL);
      console.log('🎯 Inbound signal expired, removed');
      return null;
    }

    return signal;
  } catch (error) {
    console.error('Failed to get inbound signal:', error);
    return null;
  }
};

/**
 * Clear the inbound signal
 */
export const clearInboundSignal = () => {
  localStorage.removeItem(STORAGE_KEYS.INBOUND_SIGNAL);
};

// ============================================================
// Task 2: Behavior Tracker (On-site Intent)
// ============================================================

/**
 * Get the current user profile with time-decayed scores
 * Scores decay by 50% every 7 days to prioritize recent behavior
 */
export const getUserProfile = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
    if (!stored) {
      return {
        categoryScores: {},
        totalInteractions: 0,
        lastUpdated: null
      };
    }
    const profile = JSON.parse(stored);

    // Apply time decay to scores (half-life of 7 days)
    if (profile.lastUpdated && profile.categoryScores) {
      const daysSinceUpdate = (Date.now() - new Date(profile.lastUpdated).getTime()) / (1000 * 60 * 60 * 24);
      const decayFactor = Math.pow(0.5, daysSinceUpdate / 7); // 50% decay per week

      // Only apply decay if significant time has passed (> 1 day)
      if (daysSinceUpdate > 1) {
        Object.keys(profile.categoryScores).forEach(category => {
          profile.categoryScores[category] = Math.round(profile.categoryScores[category] * decayFactor * 100) / 100;
          // Remove categories with negligible scores
          if (profile.categoryScores[category] < 0.5) {
            delete profile.categoryScores[category];
          }
        });
      }
    }

    return profile;
  } catch (error) {
    console.error('Failed to get user profile:', error);
    return { categoryScores: {}, totalInteractions: 0, lastUpdated: null };
  }
};

/**
 * Save the user profile
 */
const saveUserProfile = (profile) => {
  try {
    profile.lastUpdated = new Date().toISOString();
    localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile));
  } catch (error) {
    console.error('Failed to save user profile:', error);
  }
};

// Cross-category affinity — related categories get a fraction of the score
const CATEGORY_AFFINITY = {
  gaming: { tech: 0.8, art: 0.3 },
  tech: { gaming: 0.7 },
  fashion: { beauty: 0.7, jewelry: 0.6 },
  beauty: { fashion: 0.5, wellness: 0.4 },
  food: { home: 0.5 },
  sports: { outdoor: 0.8, wellness: 0.5 },
  outdoor: { sports: 0.6, travel: 0.4 },
  travel: { outdoor: 0.4 },
  home: { food: 0.4 },
  jewelry: { fashion: 0.5 },
  wellness: { beauty: 0.4, sports: 0.3 },
  kids: { toys: 0.8 },
};

// Recipient → default category affinities (used for cold-start)
const RECIPIENT_DEFAULTS = {
  mother: ['beauty', 'home', 'jewelry', 'food'],
  father: ['tech', 'sports', 'outdoor'],
  girlfriend: ['fashion', 'beauty', 'jewelry'],
  boyfriend: ['tech', 'gaming', 'sports'],
  grandmother: ['home', 'food', 'beauty'],
  grandfather: ['outdoor', 'books', 'home'],
  friend: ['food', 'books', 'music'],
  child: ['gaming', 'kids', 'books'],
  sibling: ['tech', 'fashion', 'music'],
  colleague: ['food', 'books', 'home'],
};

/**
 * Apply recipient defaults for cold-start personalization
 * Called when quiz step 1 (recipient) is answered
 */
export const applyRecipientDefaults = (recipient) => {
  const defaults = RECIPIENT_DEFAULTS[recipient];
  if (!defaults) return;
  defaults.forEach(cat => addCategoryScore(cat, 1)); // mild +1 boost
};

/**
 * Add score to a category (with cross-category affinity)
 */
const addCategoryScore = (category, score) => {
  const normalizedCategory = normalizeCategory(category);
  if (!normalizedCategory) return null;

  const profile = getUserProfile();

  if (!profile.categoryScores[normalizedCategory]) {
    profile.categoryScores[normalizedCategory] = 0;
  }

  profile.categoryScores[normalizedCategory] += score;

  // Apply cross-category affinity (only for positive scores > 1)
  if (score > 1) {
    const affinities = CATEGORY_AFFINITY[normalizedCategory];
    if (affinities) {
      Object.entries(affinities).forEach(([relatedCat, factor]) => {
        if (!profile.categoryScores[relatedCat]) profile.categoryScores[relatedCat] = 0;
        profile.categoryScores[relatedCat] += Math.round(score * factor * 10) / 10;
      });
    }
  }

  profile.totalInteractions += 1;

  saveUserProfile(profile);
  console.log(`📊 Category score updated: ${normalizedCategory} = ${profile.categoryScores[normalizedCategory]}`);

  return profile;
};

/**
 * Track a deal click
 * Call this when a user clicks on a deal in a specific category
 * Adds +2 points to the category
 */
export const trackDealClick = (category) => {
  if (!category) return;
  return addCategoryScore(category, CLICK_SCORE);
};

/**
 * Track quiz completion
 * Call this when a user completes the quiz
 * Adds +3 points to each selected interest
 */
export const trackQuizCompletion = (interests) => {
  if (!interests || !Array.isArray(interests)) return;

  interests.forEach(interest => {
    addCategoryScore(interest, QUIZ_RESULT_SCORE);
  });

  console.log(`📊 Quiz interests tracked: ${interests.join(', ')}`);
  return getUserProfile();
};

/**
 * Track a buy click (strongest signal — user committed to purchasing)
 * Adds +5 points to the category
 */
export const trackBuyClick = (category) => {
  if (!category) return;
  return addCategoryScore(category, BUY_CLICK_SCORE);
};

/**
 * Track gift save/bookmark (+3 points)
 */
export const trackSave = (category) => {
  if (!category) return;
  return addCategoryScore(category, SAVE_SCORE);
};

/**
 * Track swipe left / skip (mild negative signal)
 */
export const trackSkip = (category) => {
  if (!category) return;
  return addCategoryScore(category, SWIPE_LEFT_SCORE);
};

/**
 * Save quiz context for return visit personalization
 * Stores recipient, occasion, budget across sessions
 */
export const saveQuizContext = (answers) => {
  if (!answers) return;
  try {
    const profile = getUserProfile();
    // Build recipient history (last 5 unique)
    const history = profile.recipientHistory || [];
    if (answers.recipient && !history.includes(answers.recipient)) {
      history.unshift(answers.recipient);
      if (history.length > 5) history.pop();
    }
    profile.recipientHistory = history;
    profile.lastRecipient = answers.recipient || profile.lastRecipient;
    profile.lastOccasion = answers.occasion || profile.lastOccasion;
    profile.budgetPreference = answers.budget || profile.budgetPreference;
    profile.visitCount = (profile.visitCount || 0) + 1;
    saveUserProfile(profile);
    console.log(`📊 Quiz context saved: ${answers.recipient}, ${answers.occasion}, ${answers.budget}`);
  } catch (e) {
    console.error('Failed to save quiz context:', e);
  }
};

/**
 * Get the top scoring categories
 */
export const getTopCategories = (limit = 3) => {
  const profile = getUserProfile();
  const scores = profile.categoryScores;

  return Object.entries(scores)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([category, score]) => ({ category, score }));
};

/**
 * Get the highest scoring category
 */
export const getTopCategory = () => {
  const top = getTopCategories(1);
  return top.length > 0 ? top[0].category : null;
};

// ============================================================
// Task 3: Dynamic Result Re-ordering (Personalization Logic)
// ============================================================

/**
 * Determine which category should be boosted
 * Priority: Inbound Signal > Highest Scoring Category
 */
export const getBoostCategory = () => {
  // First priority: Inbound signal from ads
  const inboundSignal = getInboundSignal();
  if (inboundSignal && inboundSignal.category) {
    return {
      category: inboundSignal.category,
      source: 'inbound',
      originalSource: inboundSignal.source
    };
  }

  // Second priority: Highest scoring category from behavior
  const topCategory = getTopCategory();
  if (topCategory) {
    return {
      category: topCategory,
      source: 'behavior',
      originalSource: null
    };
  }

  return null;
};

/**
 * Check if an item matches the boost category
 * @param {Object} item - Deal or gift object
 * @param {string} boostCategory - Category to match
 * @returns {boolean}
 */
const itemMatchesCategory = (item, boostCategory) => {
  if (!item || !boostCategory) return false;

  // Check explicit category fields first
  const itemCategories = [
    item.category,
    item.displayCategory,
    item.occasionValue,
    item.interest,
    ...(item.categories || []),
    ...(item.tags || []),
    ...(item.interests || [])
  ].filter(Boolean).map(c => normalizeCategory(c));

  if (itemCategories.includes(boostCategory)) {
    return true;
  }

  // Use enhanced category detection from title/description
  const detectedCategories = detectItemCategories(item);
  if (detectedCategories.includes(boostCategory)) {
    return true;
  }

  // Fallback: simple keyword check
  const searchText = `${item.title || ''} ${item.description || ''}`.toLowerCase();
  const categoryKeywords = getCategoryKeywords(boostCategory);
  const hasKeywordMatch = categoryKeywords.some(keyword => searchText.includes(keyword.toLowerCase()));

  return hasKeywordMatch;
};

/**
 * Get keywords associated with a category for fuzzy matching
 * Extended with more product-specific terms for better gift matching
 */
const getCategoryKeywords = (category) => {
  const keywordMap = {
    tech: [
      'tech', 'gadget', 'electronic', 'smart', 'device', 'phone', 'laptop', 'tablet',
      'watch', 'headphone', 'speaker', 'charger', 'wireless', 'bluetooth', 'usb',
      'cable', 'adapter', 'power bank', 'drone', 'camera', 'lens', 'tripod', 'led',
      'keyboard', 'mouse', 'monitor', 'vr', 'airpods', 'earbuds', 'smartwatch',
      'fitbit', 'garmin', 'apple', 'samsung', 'iphone', 'android', 'alexa', 'echo'
    ],
    fashion: [
      'fashion', 'style', 'clothing', 'dress', 'shirt', 'pants', 'shoes', 'bag',
      'purse', 'accessory', 'jacket', 'wear', 'coat', 'sweater', 'hoodie', 'jeans',
      'sneaker', 'boots', 'heels', 'sandal', 'wallet', 'belt', 'scarf', 'hat',
      'sunglasses', 'handbag', 'tote', 'clutch', 'designer', 'leather', 'cashmere'
    ],
    sports: [
      'sport', 'fitness', 'workout', 'exercise', 'gym', 'running', 'yoga', 'athletic',
      'training', 'weights', 'dumbbell', 'resistance', 'bike', 'cycling', 'swimming',
      'tennis', 'golf', 'basketball', 'soccer', 'football', 'baseball', 'hiking',
      'climbing', 'protein', 'shaker', 'water bottle', 'mat', 'foam roller'
    ],
    books: [
      'book', 'reading', 'novel', 'kindle', 'journal', 'planner', 'diary', 'notebook',
      'bestseller', 'author', 'fiction', 'non-fiction', 'biography', 'memoir',
      'self-help', 'recipe book', 'cookbook', 'art book', 'photography book'
    ],
    food: [
      'food', 'cook', 'kitchen', 'chef', 'grill', 'bake', 'recipe', 'culinary',
      'coffee', 'tea', 'wine', 'chocolate', 'gourmet', 'spice', 'sauce', 'snack',
      'candy', 'gift basket', 'cheese', 'olive oil', 'honey', 'jam', 'bbq',
      'espresso', 'latte', 'brewing', 'cocktail', 'whiskey', 'bourbon'
    ],
    gaming: [
      'game', 'gaming', 'console', 'controller', 'playstation', 'xbox', 'nintendo',
      'switch', 'pc gaming', 'steam', 'headset', 'mouse pad', 'rgb', 'mechanical',
      'esports', 'streamer', 'twitch', 'gamer', 'video game', 'retro game'
    ],
    art: [
      'art', 'craft', 'creative', 'paint', 'draw', 'sketch', 'diy', 'handmade',
      'canvas', 'brush', 'pencil', 'marker', 'watercolor', 'acrylic', 'pottery',
      'sculpture', 'print', 'frame', 'easel', 'calligraphy', 'lettering', 'sewing',
      'knitting', 'crochet', 'embroidery', 'scrapbook'
    ],
    travel: [
      'travel', 'luggage', 'suitcase', 'backpack', 'adventure', 'camping', 'outdoor',
      'hiking', 'passport', 'packing', 'carry-on', 'duffel', 'toiletry', 'organizer',
      'neck pillow', 'eye mask', 'adapter', 'portable', 'compact', 'foldable'
    ],
    music: [
      'music', 'instrument', 'guitar', 'piano', 'speaker', 'headphone', 'vinyl',
      'record', 'turntable', 'ukulele', 'keyboard', 'drum', 'microphone', 'amp',
      'spotify', 'playlist', 'concert', 'band', 'dj', 'mixer', 'synthesizer'
    ],
    beauty: [
      'beauty', 'skincare', 'makeup', 'cosmetic', 'cream', 'serum', 'facial', 'spa',
      'moisturizer', 'cleanser', 'toner', 'mask', 'lipstick', 'eyeshadow', 'mascara',
      'foundation', 'blush', 'perfume', 'fragrance', 'cologne', 'lotion', 'body',
      'hair', 'shampoo', 'conditioner', 'styling', 'brush', 'mirror', 'vanity'
    ],
    home: [
      'home', 'decor', 'furniture', 'kitchen', 'living', 'bedroom', 'bathroom',
      'pillow', 'blanket', 'throw', 'candle', 'diffuser', 'vase', 'plant', 'pot',
      'frame', 'clock', 'lamp', 'light', 'rug', 'curtain', 'organizer', 'storage'
    ],
    outdoor: [
      'outdoor', 'garden', 'camping', 'hiking', 'nature', 'patio', 'bbq', 'grill',
      'tent', 'sleeping bag', 'lantern', 'cooler', 'hammock', 'chair', 'fire pit',
      'bird', 'feeder', 'planter', 'tools', 'gloves'
    ],
    wellness: [
      'wellness', 'health', 'meditation', 'relaxation', 'massage', 'aromatherapy',
      'self-care', 'essential oil', 'diffuser', 'yoga', 'mindfulness', 'journal',
      'sleep', 'weighted blanket', 'stress', 'anxiety', 'calm', 'zen', 'therapy'
    ],
    jewelry: [
      'jewelry', 'necklace', 'bracelet', 'earring', 'ring', 'watch', 'gold', 'silver',
      'diamond', 'pearl', 'gemstone', 'pendant', 'chain', 'charm', 'anklet', 'cuff',
      'stud', 'hoop', 'engagement', 'wedding', 'anniversary', 'birthstone'
    ],
    kids: [
      'kid', 'child', 'toy', 'baby', 'toddler', 'educational', 'lego', 'puzzle',
      'stuffed', 'plush', 'doll', 'action figure', 'board game', 'craft kit',
      'building', 'blocks', 'learning', 'stem', 'science', 'robot'
    ],
    pets: [
      'pet', 'dog', 'cat', 'animal', 'puppy', 'kitten', 'collar', 'leash', 'bowl',
      'bed', 'toy', 'treat', 'food', 'grooming', 'brush', 'carrier', 'crate'
    ]
  };

  return keywordMap[category] || [category];
};

/**
 * Detect categories from item title and description
 * Returns array of detected categories sorted by confidence
 */
export const detectItemCategories = (item) => {
  if (!item) return [];

  const searchText = `${item.title || ''} ${item.description || ''} ${item.aiReason || ''}`.toLowerCase();
  const categoryScores = {};

  VALID_CATEGORIES.forEach(category => {
    const keywords = getCategoryKeywords(category);
    let score = 0;

    keywords.forEach(keyword => {
      // Count occurrences and weight by keyword length (longer = more specific)
      const regex = new RegExp(keyword.toLowerCase(), 'gi');
      const matches = (searchText.match(regex) || []).length;
      score += matches * (1 + keyword.length / 10);
    });

    if (score > 0) {
      categoryScores[category] = score;
    }
  });

  // Return categories sorted by score
  return Object.entries(categoryScores)
    .sort(([, a], [, b]) => b - a)
    .map(([category]) => category);
};

/**
 * Reorder items based on personalization
 * Boosts matching items to the top (positions 0-2)
 *
 * @param {Array} items - Array of deals or gifts
 * @param {number} boostCount - Number of positions to boost to (default: 3)
 * @returns {Object} { items: reorderedArray, boostedItems: Array, boostInfo: Object }
 */
export const personalizeResults = (items, boostCount = 3) => {
  if (!items || items.length === 0) {
    return { items: [], boostedItems: [], boostInfo: null };
  }

  const boostInfo = getBoostCategory();

  if (!boostInfo) {
    // No personalization needed
    return { items: [...items], boostedItems: [], boostInfo: null };
  }

  const boostCategory = boostInfo.category;

  // Separate matching and non-matching items
  const matchingItems = [];
  const nonMatchingItems = [];

  items.forEach(item => {
    if (itemMatchesCategory(item, boostCategory)) {
      matchingItems.push({ ...item, _personalized: true, _boostCategory: boostCategory });
    } else {
      nonMatchingItems.push(item);
    }
  });

  // If no matches, return original order
  if (matchingItems.length === 0) {
    return { items: [...items], boostedItems: [], boostInfo };
  }

  // Take up to boostCount matching items for the top
  const boostedItems = matchingItems.slice(0, boostCount);
  const remainingMatches = matchingItems.slice(boostCount);

  // Combine: boosted items at top, then remaining items in original-ish order
  const reorderedItems = [
    ...boostedItems,
    ...nonMatchingItems,
    ...remainingMatches
  ];

  console.log(`✨ Personalization applied: Boosted ${boostedItems.length} items for category "${boostCategory}" (source: ${boostInfo.source})`);

  return {
    items: reorderedItems,
    boostedItems,
    boostInfo
  };
};

/**
 * Check if an item was personalized/boosted
 */
export const isPersonalizedItem = (item) => {
  return item && item._personalized === true;
};

/**
 * Get the boost category of a personalized item
 */
export const getItemBoostCategory = (item) => {
  return item ? item._boostCategory : null;
};

// ============================================================
// Initialization
// ============================================================

/**
 * Initialize the personalization engine
 * Should be called once on app load
 */
export const initPersonalization = () => {
  // Capture any inbound signals from URL
  captureInboundSignal();

  // Clean up expired data
  getInboundSignal(); // This will remove expired signals

  // Log current personalization state
  const boostInfo = getBoostCategory();
  const profile = getUserProfile();

  if (boostInfo) {
    console.log(`🎯 Personalization active: boosting "${boostInfo.category}" (source: ${boostInfo.source})`);
  }

  if (profile.totalInteractions > 0) {
    console.log(`📊 User profile: ${profile.totalInteractions} interactions, top categories:`, getTopCategories());
  }

  return { boostInfo, profile };
};

// Default export for convenience
const personalization = {
  // Initialization
  init: initPersonalization,

  // Signal Listener
  captureInboundSignal,
  getInboundSignal,
  clearInboundSignal,

  // Behavior Tracker
  getUserProfile,
  trackDealClick,
  trackQuizCompletion,
  getTopCategories,
  getTopCategory,

  // Personalization Logic
  getBoostCategory,
  personalizeResults,
  isPersonalizedItem,
  getItemBoostCategory,

  // Constants
  VALID_CATEGORIES
};

export default personalization;
